(function () {
  'use strict';

  const SELECTOR = '[data-footer-shorts]';
  const API_URL = 'https://www.googleapis.com/youtube/v3/search';

  class FooterShortsCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-footer-shorts-track]');
      this.viewport = root.querySelector('.shorts-rail__viewport') || root.querySelector('.footer-shorts__viewport');
      this.dotsContainer = root.querySelector('[data-footer-shorts-dots]');
      this.messageEl = root.querySelector('[data-footer-shorts-message]');
      this.statusEl = root.querySelector('[data-footer-shorts-status]');
      this.prevButton = root.querySelector('.footer-shorts__nav--prev');
      this.nextButton = root.querySelector('.footer-shorts__nav--next');
      this.apiKey = root.dataset.apiKey;
      this.channelId = root.dataset.channelId;
      this.maxResults = parseInt(root.dataset.maxResults || '', 10) || 4;
      this.intervalMs = parseInt(root.dataset.autoplayInterval || '', 10) || 8000;
      this.currentIndex = 0;
      this.slides = [];
      this.videos = [];
      this.autoplayId = null;
      this.prefersReducedMotion = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
      this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
      this.boundResizeHandler = this.handleResize.bind(this);
    }

    async init() {
      if (!this.track) {
        return;
      }

      if (!this.apiKey || !this.channelId) {
        this.showMessage('Add a YouTube API key and channel ID to show shorts.');
        return;
      }

      this.showMessage('Loading shorts...');

      try {
        this.videos = await this.fetchVideos();
      } catch (error) {
        console.error('Footer shorts request failed', error);
        this.showMessage('Unable to load shorts right now.');
        return;
      }

      if (!this.videos.length) {
        this.showMessage('No shorts published yet.');
        return;
      }

      this.renderSlides();
      this.enableScrollMode();
      this.showMessage('');
    }

    async fetchVideos() {
      const params = new URLSearchParams({
        key: this.apiKey,
        channelId: this.channelId,
        part: 'snippet',
        order: 'date',
        maxResults: String(this.maxResults),
        type: 'video',
        videoDuration: 'short',
        videoEmbeddable: 'true'
      });

      const response = await fetch(`${API_URL}?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`YouTube API error: ${response.status}`);
      }

      const payload = await response.json();
      const items = Array.isArray(payload.items) ? payload.items : [];

      return items
        .map((item) => ({
          id: item?.id?.videoId,
          title: item?.snippet?.title || 'Untitled short',
          description: item?.snippet?.description || '',
          publishedAt: item?.snippet?.publishedAt,
          thumbnail: item?.snippet?.thumbnails?.high?.url || item?.snippet?.thumbnails?.default?.url
        }))
        .filter((video) => Boolean(video.id));
    }

    renderSlides() {
      this.track.innerHTML = '';

      if (this.dotsContainer) {
        this.dotsContainer.innerHTML = '';
      }

      this.slides = this.videos.map((video, index) => {
        const slide = this.createSlide(video);
        slide.dataset.index = String(index);
        this.track.appendChild(slide);
        if (this.dotsContainer) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'footer-shorts__dot';
          dot.dataset.index = String(index);
          dot.setAttribute('aria-label', `Go to short ${index + 1}`);
          dot.addEventListener('click', () => {
            this.stopAutoplay();
            this.goTo(index);
            this.startAutoplay();
          });
          this.dotsContainer.appendChild(dot);
        }
        return slide;
      });

      const hideControls = this.slides.length <= 1;
      this.toggleControls(hideControls);
    }

    enableScrollMode() {
      this.stopAutoplay();
      if (this.dotsContainer) this.dotsContainer.style.display = 'none';
      if (this.track) {
        this.track.style.transform = 'none';
        this.track.style.transition = 'none';
      }

      const step = () => {
        if (!this.viewport) return 0;
        return Math.max(this.viewport.clientWidth * 0.8, 200);
      };

      const maxScrollDistance = () => {
        if (!this.viewport) return 0;
        const trackWidth = this.track?.scrollWidth || this.viewport.scrollWidth;
        const lastSlide = this.slides[this.slides.length - 1];
        const lastWidth = lastSlide ? lastSlide.getBoundingClientRect().width : 0;
        // Add half a slide width so the last slide can center in view.
        return Math.max(trackWidth - this.viewport.clientWidth + lastWidth * 0.5, 0);
      };

      const scrollViewport = (direction) => {
        if (!this.viewport) return;
        const delta = direction * step();
        const maxScroll = maxScrollDistance();
        const target = Math.min(Math.max(this.viewport.scrollLeft + delta, 0), maxScroll);
        this.viewport.scrollTo({ left: target, behavior: 'smooth' });
      };

      if (this.prevButton) {
        this.prevButton.style.display = '';
        this.prevButton.addEventListener('click', () => scrollViewport(-1));
      }

      if (this.nextButton) {
        this.nextButton.style.display = '';
        this.nextButton.addEventListener('click', () => scrollViewport(1));
      }
    }

    createSlide(video) {
      const slide = document.createElement('article');
      slide.className = 'footer-shorts__slide';
      slide.setAttribute('role', 'listitem');

      const player = document.createElement('div');
      player.className = 'footer-shorts__player';

      const iframe = document.createElement('iframe');
      iframe.dataset.embed = this.buildEmbedUrl(video.id);
      iframe.title = `${video.title} | YouTube Shorts preview`;
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      // For scroll-mode, set src immediately so videos load without navigation
      iframe.src = iframe.dataset.embed;
      player.appendChild(iframe);

      const overlayLink = document.createElement('a');
      overlayLink.className = 'footer-shorts__overlay-link';
      overlayLink.href = `https://www.youtube.com/shorts/${video.id}`;
      overlayLink.target = '_blank';
      overlayLink.rel = 'noopener';
      overlayLink.setAttribute('aria-label', `Open ${video.title} on YouTube`);
      player.appendChild(overlayLink);

      const meta = document.createElement('div');
      meta.className = 'footer-shorts__meta';

      const title = document.createElement('p');
      title.className = 'footer-shorts__title';
      title.textContent = video.title;

      const date = document.createElement('p');
      date.className = 'footer-shorts__date';
      date.textContent = this.formatDate(video.publishedAt);

      meta.appendChild(title);
      meta.appendChild(date);

      slide.appendChild(player);
      slide.appendChild(meta);

      return slide;
    }

    buildEmbedUrl(videoId) {
      const base = `https://www.youtube.com/embed/${videoId}`;
      const params = new URLSearchParams({
        autoplay: '1',
        loop: '1',
        mute: '1',
        playsinline: '1',
        controls: '0',
        rel: '0',
        modestbranding: '1',
        playlist: videoId
      });
      return `${base}?${params.toString()}`;
    }

    attachEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(this.currentIndex - 1);
          this.startAutoplay();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(this.currentIndex + 1);
          this.startAutoplay();
        });
      }

      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          if (!this.root.contains(document.activeElement)) {
            this.startAutoplay();
          }
        });
      });

      document.addEventListener('visibilitychange', this.boundVisibilityHandler);
      window.addEventListener('resize', this.boundResizeHandler);

      if (this.prefersReducedMotion && typeof this.prefersReducedMotion.addEventListener === 'function') {
        this.prefersReducedMotion.addEventListener('change', () => {
          if (this.prefersReducedMotion.matches) {
            this.stopAutoplay();
          } else {
            this.startAutoplay();
          }
        });
      }
    }

    goTo(targetIndex) {
      if (!this.slides.length) {
        return;
      }

      const total = this.slides.length;
      const normalized = ((targetIndex % total) + total) % total;
      const previousIndex = this.currentIndex;
      this.currentIndex = normalized;

      this.loadSlide(this.currentIndex);
      if (previousIndex !== this.currentIndex) {
        this.resetSlide(previousIndex);
      }

      this.updateTrack();
      this.updateDots();
      this.updateStatus();
    }

    setActiveSlide(index, instant) {
      this.currentIndex = index;
      this.loadSlide(index);
      this.updateTrack(instant);
      this.updateDots();
      this.updateStatus();
    }

    updateTrack(skipAnimation) {
      if (!this.track) {
        return;
      }

      if (skipAnimation) {
        this.track.style.transition = 'none';
      }

      const slideWidth = this.slides[0]?.getBoundingClientRect().width || 0;
      const gap = 0; // gap is handled via padding inside slides
      const offset = this.currentIndex * (slideWidth + gap);
      this.track.style.transform = `translateX(-${offset}px)`;

      if (skipAnimation) {
        requestAnimationFrame(() => {
          this.track.style.removeProperty('transition');
        });
      }
    }

    updateDots() {
      if (!this.dotsContainer) {
        return;
      }

      const dots = Array.from(this.dotsContainer.querySelectorAll('.footer-shorts__dot'));
      dots.forEach((dot) => {
        const dotIndex = parseInt(dot.dataset.index || '', 10);
        if (dotIndex === this.currentIndex) {
          dot.classList.add('is-active');
        } else {
          dot.classList.remove('is-active');
        }
      });
    }

    updateStatus() {
      if (!this.statusEl || !this.videos.length) {
        return;
      }
      this.statusEl.textContent = `Short ${this.currentIndex + 1} of ${this.videos.length}`;
    }

    loadSlide(index) {
      const slide = this.slides[index];
      if (!slide) {
        return;
      }
      const iframe = slide.querySelector('iframe');
      if (iframe && !iframe.src) {
        iframe.src = iframe.dataset.embed;
      }
    }

    resetSlide(index) {
      const slide = this.slides[index];
      if (!slide) {
        return;
      }
      const iframe = slide.querySelector('iframe');
      if (iframe && iframe.src) {
        iframe.src = '';
      }
    }

    toggleControls(hide) {
      const disabled = hide;
      if (this.prevButton) {
        this.prevButton.disabled = disabled;
        this.prevButton.hidden = hide;
      }
      if (this.nextButton) {
        this.nextButton.disabled = disabled;
        this.nextButton.hidden = hide;
      }
      if (this.dotsContainer) {
        this.dotsContainer.hidden = hide;
      }
    }

    startAutoplay() {
      if (this.slides.length <= 1) {
        return;
      }
      if (document.hidden || (this.prefersReducedMotion && this.prefersReducedMotion.matches)) {
        return;
      }
      this.stopAutoplay();
      this.autoplayId = window.setInterval(() => {
        this.goTo(this.currentIndex + 1);
      }, this.intervalMs);
    }

    stopAutoplay() {
      if (this.autoplayId) {
        window.clearInterval(this.autoplayId);
        this.autoplayId = null;
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleResize() {
      this.updateTrack(true);
    }

    showMessage(text) {
      if (!this.messageEl) {
        return;
      }
      if (text) {
        this.messageEl.hidden = false;
        this.messageEl.textContent = text;
      } else {
        this.messageEl.hidden = true;
        this.messageEl.textContent = '';
      }
    }

    formatDate(value) {
      if (!value) {
        return '';
      }
      try {
        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        return formatter.format(new Date(value));
      } catch (error) {
        console.warn('Unable to format date', error);
        return new Date(value).toLocaleDateString();
      }
    }
  }

  function initShorts() {
    const blocks = document.querySelectorAll(SELECTOR);
    blocks.forEach((block) => {
      if (block.dataset.footerShortsInitialized) {
        return;
      }
      block.dataset.footerShortsInitialized = 'true';
      const carousel = new FooterShortsCarousel(block);
      carousel.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShorts);
  } else {
    initShorts();
  }
})();
